import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useCallback} from 'react';

import {
  type SettingType,
  trackSetting,
} from '../../services/analytics/workspaceSettingsEvents';
import * as ViewHooks from '../../state/views/hooks';
import * as PanelSettingsActions from '../../state/views/panelSettings/actions';
import type * as PanelSettingsViewTypes from '../../state/views/panelSettings/types';
import {type SettingOverrideKey} from '../../state/views/workspaceSettings/settingOverrideUtils';
import * as RunHelpers from '../../util/runhelpers';
import {useKeyInfoQueryContext} from '../MultiRunWorkspace/KeyInfoQueryContext';
import {type LinePlotPanel} from '../PanelRunsLinePlot/types';
import {getXAxisChoices} from '../PanelRunsLinePlot/utils/axis';
import {useLinePlotSettingsWithDefaults} from '../WorkspaceDrawer/Settings/runLinePlots/utils/getLinePlotSettings';
import {
  type ExcludeOutliersValues,
  type LinePlotSettings,
} from '../WorkspaceDrawer/Settings/types';
import {
  areOutliersExcluded,
  areSmoothingSettingsActive,
  areXAxisSettingsActive,
  xAxisLabel,
} from '../WorkspaceDrawer/Settings/utils';
import {ExcludeOutliers} from './ExcludeOutliers';
import {SmoothingSetting} from './SmoothingSetting';
import {XAxisSetting} from './XAxisSetting';

export type ReportSettingsProps = {
  workspacePanelSettingsRef: PanelSettingsViewTypes.Ref;

  // This is an expensive hook to call! It can inspect the entire
  // panelbank config (huge if there are many metrics), or report.
  // So only call it when we need to (when the user has opened the
  // smoothing popup).
  useAllLinePlotPanels(): LinePlotPanel[];
};

export const ReportSettings = ({
  workspacePanelSettingsRef,
  useAllLinePlotPanels,
}: ReportSettingsProps) => {
  const workspacePanelSettings = ViewHooks.usePart(workspacePanelSettingsRef);

  const updatePanelSettings = ViewHooks.useViewAction(
    workspacePanelSettingsRef,
    PanelSettingsActions.update
  );

  const {smoothingType, smoothingWeight, xAxis, xAxisMax, xAxisMin, xLogScale} =
    useLinePlotSettingsWithDefaults(workspacePanelSettings);

  // TODO - add analytics
  const useXAxisOptions = () => {
    const {
      keyInfoQuery: {historyKeyInfo: keyInfo, loading},
    } = useKeyInfoQueryContext();

    const allLinePlotPanels = useAllLinePlotPanels();

    const configMetrics = React.useMemo(() => {
      if (loading) {
        return [];
      }

      const result: string[] = [];
      const set = new Set<string>();
      for (const panel of allLinePlotPanels) {
        for (const m of panel.config.metrics ?? []) {
          if (!set.has(m)) {
            set.add(m);
            result.push(m);
          }
        }
      }

      return result;
    }, [allLinePlotPanels, loading]);

    const keyTypes = React.useMemo(() => {
      return RunHelpers.keyTypes(keyInfo.keys);
    }, [keyInfo]);

    const allXAxes = React.useMemo(() => {
      return getXAxisChoices({
        configMetrics,
        configXAxis: xAxis,
        keyInfo,
        keyTypes,
      });
    }, [configMetrics, keyInfo, keyTypes]);

    const xAxisOptions = React.useMemo(() => {
      return allXAxes.map(xAxis => ({
        value: xAxis,
        label: xAxisLabel(xAxis),
      }));
    }, [allXAxes]);

    return xAxisOptions;
  };

  const clearSetting = useCallback(
    (
      type: 'workspace' | 'section' | 'panel' | 'report',
      settingKey: SettingOverrideKey,
      defaults: Partial<LinePlotSettings>
    ) => {
      updatePanelSettings(defaults);
    },
    [updatePanelSettings]
  );

  return (
    <Tailwind
      style={{
        display: 'flex',
        gap: '8px',
      }}>
      <XAxisSetting
        clearSetting={clearSetting}
        displayValues={{
          xAxis,
          xAxisMax,
          xAxisMin,
          xLogScale,
        }}
        isActive={areXAxisSettingsActive(workspacePanelSettings, true)}
        trackSetting={trackReportSettingChange}
        settingLevel="report"
        updateSetting={updatePanelSettings}
        useXAxisOptions={useXAxisOptions}
        values={{
          xAxis: workspacePanelSettings.xAxis,
          xAxisMax: workspacePanelSettings.xAxisMax,
          xAxisMin: workspacePanelSettings.xAxisMin,
          // Note: intentionally ignoring xLogScale here, because it's not
          // a setting that can be overridden in the report settings right now
        }}
      />
      <SmoothingSetting
        clearSetting={clearSetting}
        displayValues={{
          smoothingType,
          smoothingWeight,
        }}
        isActive={areSmoothingSettingsActive(workspacePanelSettings, true)}
        trackSetting={trackReportSettingChange}
        settingLevel="report"
        updateSetting={updatePanelSettings}
        values={{
          smoothingType: workspacePanelSettings.smoothingType,
          smoothingWeight: workspacePanelSettings.smoothingWeight,
        }}
      />
      <ExcludeOutliers
        isActive={areOutliersExcluded(workspacePanelSettings.excludeOutliers)}
        buttonVariant="secondary"
        onClick={(newVal: ExcludeOutliersValues) => {
          updatePanelSettings({
            excludeOutliers: newVal,
          });
          trackSetting({
            action: 'change',
            page: 'report',
            settingLevel: 'report',
            setting: 'excludeOutliers',
            settingValue: newVal.toString(),
          });
        }}
        popupProps={{
          position: 'bottom left',
          inverted: true,
          size: 'mini',
        }}
      />
    </Tailwind>
  );
};

const trackReportSettingChange = (
  setting: SettingType,
  value: string | undefined
) => {
  trackSetting({
    action: 'change',
    page: 'report',
    settingLevel: 'report',
    setting,
    settingValue: value,
  });
};
